import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { useListAllChatsQuery } from "../../../store/queries/chat";
import { IChat } from "../../../types/Chat";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LinkText = styled.span`
  color: #0066cc;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

interface IPaginatedChatsTableProps {
  modelId?: number;
  accountId?: string;
  showFilters?: boolean;
  defaultPageSize?: number;
}

function PaginatedChatsTable({
  modelId,
  accountId,
  showFilters = false,
  defaultPageSize = 25,
}: IPaginatedChatsTableProps) {
  const navigate = useNavigate();

  // Fetch all data at once for client-side filtering and pagination
  const { data, isLoading, error } = useListAllChatsQuery({
    page: 1,
    limit: 1000, // Get a large number to get all chats
    modelId,
    accountId,
  });

  const parsedChats = useMemo(() => {
    if (!data?.chats) return [];
    return data.chats.map((chat: IChat) => ({
      id: chat.id,
      accountId: chat.accountId,
      accountName: chat.account.name,
      orderId: chat.data?.orderId,
      chatModelId: chat.chatModelId,
      chatModelName: chat.chatModel.name,
      messageCount: chat.chatMessages?.length || 0,
      createdAt: new Date(chat.createdAt).toLocaleDateString(),
      updatedAt: new Date(chat.updatedAt).toLocaleDateString(),
      createdAtDate: new Date(chat.createdAt),
    }));
  }, [data?.chats]);

  const accountLinkBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText
          onClick={() => navigate(`/hospitals/view/${rowData.accountId}`)}
        >
          {rowData.accountName}
        </LinkText>
      );
    },
    [navigate]
  );

  const orderLinkBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText onClick={() => navigate(`/orders/${rowData.orderId}`)}>
          {rowData.orderId}
        </LinkText>
      );
    },
    [navigate]
  );

  const modelLinkBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText
          onClick={() => navigate(`/chatbot/models/${rowData.chatModelId}`)}
        >
          {rowData.chatModelName}
        </LinkText>
      );
    },
    [navigate]
  );

  const chatIdBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText onClick={() => navigate(`/chatbot/chats/${rowData.id}`)}>
          {rowData.id}
        </LinkText>
      );
    },
    [navigate]
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div>
        <p>Error loading chats. Please try again.</p>
        <Button label="Retry" onClick={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <Container>
      <DataTable
        stripedRows
        className="p-fluid"
        filterDisplay={showFilters ? "row" : undefined}
        value={parsedChats}
        responsiveLayout="scroll"
        emptyMessage="No chat sessions to show."
        paginator
        rows={defaultPageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      >
        <Column
          field="id"
          style={{ minWidth: "100px", maxWidth: "120px" }}
          header="Chat ID"
          body={chatIdBody}
        />
        {!accountId && (
          <Column
            filter={showFilters}
            filterMatchMode={FilterMatchMode.CONTAINS}
            field="accountName"
            style={{ minWidth: "150px" }}
            header="Account"
            filterPlaceholder="Search by account"
            body={accountLinkBody}
          />
        )}
        <Column
          filter={showFilters}
          filterMatchMode={FilterMatchMode.CONTAINS}
          field="orderId"
          style={{ minWidth: "150px" }}
          header="Order"
          filterPlaceholder="Search by order ID"
          body={orderLinkBody}
        />
        {!modelId && (
          <Column
            filter={showFilters}
            filterMatchMode={FilterMatchMode.CONTAINS}
            field="chatModelName"
            style={{ minWidth: "120px", maxWidth: "150px" }}
            header="Model"
            filterPlaceholder="Search by model name"
            body={modelLinkBody}
          />
        )}
        <Column
          field="messageCount"
          style={{ minWidth: "100px", maxWidth: "120px" }}
          header="Messages"
          dataType="numeric"
        />
        <Column
          field="createdAt"
          style={{ minWidth: "120px", maxWidth: "150px" }}
          header="Created"
        />
        <Column
          field="updatedAt"
          style={{ minWidth: "120px", maxWidth: "150px" }}
          header="Last Updated"
        />
      </DataTable>
    </Container>
  );
}

export { PaginatedChatsTable };
